'use client';
import { CompletedStandaloneMediaAsset, MediaAssetStatus, StandaloneMediaAsset, MediaAssetType } from '@/types/media';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as MediaAssetAPI from '@/api/media-asset';
import { useQuery } from '@tanstack/react-query';
import { getOutputBucketUrl } from '@/utils/public-urls';
import usePrevious from '@tolstoy/shared-react/hooks/usePrevious';
import { useCurrentApp } from '@/stores/userDataStore';
import { differenceInMinutes } from 'date-fns';

type MediaAssetPollingContextData = {
  pendingMediaAssets: StandaloneMediaAsset[];
  failedMediaAssets: StandaloneMediaAsset[];
  completedMediaAssets: CompletedStandaloneMediaAsset[];
  addMediaAssetToPollingQueue: (mediaAsset: StandaloneMediaAsset | StandaloneMediaAsset[] | null) => void;
  setPollingQueue: (mediaAssets: StandaloneMediaAsset[]) => void;
  initializeFailedMediaAssets: () => void;
};

const MediaAssetPollingContext = createContext<MediaAssetPollingContextData | null>(null);

const POLLING_INTERVAL = 5_000;

type MediaAssetPollingProviderProps = {
  children: React.ReactNode;
  onCompletion?: (mediaAssets: CompletedStandaloneMediaAsset[]) => void;
};

const isCompletedStandaloneMediaAsset = (
  mediaAsset: StandaloneMediaAsset
): mediaAsset is CompletedStandaloneMediaAsset => {
  if (mediaAsset.status === MediaAssetStatus.DONE) {
    if (mediaAsset.type === MediaAssetType.image && !!mediaAsset.result) {
      (mediaAsset as CompletedStandaloneMediaAsset).src = `${getOutputBucketUrl()}/${mediaAsset.result}`;
    }
    return true;
  }
  return false;
};

export const MediaAssetPollingProvider = memo(({ children, onCompletion }: MediaAssetPollingProviderProps) => {
  const { isLoggedIn } = useCurrentApp();
  const [completedMediaAssets, setCompletedMediaAssets] = useState<CompletedStandaloneMediaAsset[]>([]);
  const [failedMediaAssets, setFailedMediaAssets] = useState<StandaloneMediaAsset[]>([]);
  const [pendingMediaAssets, setPendingMediaAssets] = useState<StandaloneMediaAsset[]>([]);

  const addMediaAsset = useCallback(
    (mediaAssets: CompletedStandaloneMediaAsset[]) => {
      setCompletedMediaAssets((prev) => [...prev, ...mediaAssets]);
      if (onCompletion) onCompletion(mediaAssets);
    },
    [setCompletedMediaAssets, onCompletion]
  );

  const setPollingQueue = useCallback(
    (mediaAssets: StandaloneMediaAsset[]) => {
      setPendingMediaAssets(mediaAssets);
    },
    [setPendingMediaAssets]
  );

  const addMediaAssetToPollingQueue = useCallback(
    (mediaAsset: StandaloneMediaAsset | StandaloneMediaAsset[] | null) => {
      if (!mediaAsset) return;
      if (Array.isArray(mediaAsset)) {
        setPendingMediaAssets((prev) => [...prev, ...mediaAsset]);
      } else {
        setPendingMediaAssets((prev) => [...prev, mediaAsset]);
      }
    },
    [setPendingMediaAssets]
  );

  const initializeFailedMediaAssets = useCallback(() => {
    setFailedMediaAssets([]);
  }, [setFailedMediaAssets]);

  const pollingQuery = useQuery({
    queryKey: ['media-asset-polling', ...pendingMediaAssets.map((mediaAsset) => mediaAsset.id)],
    queryFn: async () =>
      MediaAssetAPI.getMediaAssetByIds({
        mediaAssetIds: pendingMediaAssets,
      }),
    enabled:
      isLoggedIn &&
      pendingMediaAssets.length > 0 &&
      pendingMediaAssets.every((mediaAsset) => differenceInMinutes(new Date(), new Date(mediaAsset.updatedAt)) < 60),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    refetchInterval: ({ state }) => {
      const { data } = state;
      if (!data) return POLLING_INTERVAL;
      if (data.mediaAssets.some((mediaAsset) => mediaAsset.status === MediaAssetStatus.PROCESSING)) {
        return POLLING_INTERVAL;
      }
      return false;
    },
  });

  const prevPollingData = usePrevious(pollingQuery.data);

  useEffect(() => {
    if (pollingQuery.data && prevPollingData !== pollingQuery.data) {
      const completedMediaAssets: CompletedStandaloneMediaAsset[] = [];
      const failedMediaAssets: StandaloneMediaAsset[] = [];
      const newPendingMediaAssets: StandaloneMediaAsset[] = [];

      pendingMediaAssets.forEach((pendingItem) => {
        const updatedItem = pollingQuery.data.mediaAssets.find((mediaAsset) => mediaAsset.id === pendingItem.id);
        if (!updatedItem) return;

        if (isCompletedStandaloneMediaAsset(updatedItem)) {
          completedMediaAssets.push(updatedItem);
          return;
        }
        if (updatedItem.status === MediaAssetStatus.FAILED) {
          failedMediaAssets.push(updatedItem);
          return;
        }

        newPendingMediaAssets.push(pendingItem);
      });

      setPendingMediaAssets(newPendingMediaAssets);
      if (completedMediaAssets.length > 0) {
        addMediaAsset(completedMediaAssets);
      }
      if (failedMediaAssets.length > 0) {
        setFailedMediaAssets((prev) => [...prev, ...failedMediaAssets]);
      }
    }
  }, [prevPollingData, pollingQuery.data, pendingMediaAssets, addMediaAsset]);

  const contextValue = useMemo(
    () => ({
      pendingMediaAssets,
      failedMediaAssets,
      addMediaAssetToPollingQueue,
      completedMediaAssets,
      setPollingQueue,
      initializeFailedMediaAssets,
    }),
    [
      pendingMediaAssets,
      failedMediaAssets,
      addMediaAssetToPollingQueue,
      completedMediaAssets,
      setPollingQueue,
      initializeFailedMediaAssets,
    ]
  );

  return <MediaAssetPollingContext.Provider value={contextValue}>{children}</MediaAssetPollingContext.Provider>;
});

MediaAssetPollingProvider.displayName = 'MediaAssetPollingProvider';

export const useMediaAssetPollingContext = () => {
  const context = useContext(MediaAssetPollingContext);
  if (context === null) {
    throw new Error('useMediaAssetPollingContext must be used within an MediaAssetPollingProvider');
  }
  return context;
};

export const useCompletedMediaAsset = (initialMediaAsset?: StandaloneMediaAsset | StandaloneMediaAsset[]) => {
  const { pendingMediaAssets, failedMediaAssets, completedMediaAssets } = useMediaAssetPollingContext();

  const hasError = useMemo(() => {
    if (!initialMediaAsset || (Array.isArray(initialMediaAsset) && initialMediaAsset.length === 0)) return false;
    if (Array.isArray(initialMediaAsset)) {
      return initialMediaAsset.some((mediaAsset) =>
        failedMediaAssets.find((failedMediaAsset) => failedMediaAsset.id === mediaAsset.id)
      );
    }
    return failedMediaAssets.some((failedMediaAsset) => failedMediaAsset.id === initialMediaAsset.id);
  }, [initialMediaAsset, failedMediaAssets]);

  const isLoading = useMemo(() => {
    if (!initialMediaAsset || (Array.isArray(initialMediaAsset) && initialMediaAsset.length === 0)) return false;
    if (Array.isArray(initialMediaAsset)) {
      return pendingMediaAssets.some((pendingMediaAsset) =>
        initialMediaAsset.some((mediaAsset) => mediaAsset.id === pendingMediaAsset.id)
      );
    }
    return pendingMediaAssets.some((mediaAsset) => mediaAsset.id === initialMediaAsset.id);
  }, [initialMediaAsset, pendingMediaAssets]);

  const mediaAsset = useMemo(() => {
    if (!initialMediaAsset || (Array.isArray(initialMediaAsset) && initialMediaAsset.length === 0)) return null;
    const finishedMediaAssets = completedMediaAssets.concat(failedMediaAssets as CompletedStandaloneMediaAsset[]);
    if (Array.isArray(initialMediaAsset)) {
      return finishedMediaAssets.filter((finishedMediaAsset) =>
        initialMediaAsset.some((mediaAsset) => mediaAsset.id === finishedMediaAsset.id)
      );
    }
    return finishedMediaAssets.find((mediaAsset) => mediaAsset.id === initialMediaAsset.id);
  }, [completedMediaAssets, failedMediaAssets, initialMediaAsset]);

  return {
    isLoading,
    mediaAsset,
    hasError,
  };
};

export const useCompletedMediaAssets = (mediaAssetIds: string[]) => {
  const { pendingMediaAssets, failedMediaAssets, completedMediaAssets } = useMediaAssetPollingContext();

  const hasError = useMemo(
    () =>
      !!mediaAssetIds.length &&
      failedMediaAssets.some((failedMediaAsset) => mediaAssetIds.includes(failedMediaAsset.id)),
    [mediaAssetIds, failedMediaAssets]
  );

  const isLoading = useMemo(
    () => !!mediaAssetIds.length && pendingMediaAssets.some((mediaAsset) => mediaAssetIds.includes(mediaAsset.id)),
    [mediaAssetIds, pendingMediaAssets]
  );

  const finishedMediaAssets = completedMediaAssets.concat(failedMediaAssets as CompletedStandaloneMediaAsset[]);

  return {
    isLoading,
    mediaAssets: finishedMediaAssets.filter((mediaAsset) => mediaAssetIds.includes(mediaAsset.id)),
    hasError,
  };
};
